import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FileText, Loader2 } from 'lucide-react';

export const SECTIONS = [
  ['entete', 'En-tête (période, formateur, référent, centre, statut…)'],
  ['modules', 'Modules traités sur la période'],
  ['indicateurs', 'Indicateurs (présence, heures, QCM, savoir-faire)'],
  ['apprenants', 'Tableau des apprenants'],
  ['observations_individuelles', 'Observations du formateur par apprenant (texte)'],
  ['observations_groupe', 'Observations du formateur sur le groupe'],
  ['actions', 'Actions formateur (Qualiopi ind. 21)'],
  ['evaluations', 'Évaluations de la période'],
];
const STORAGE_KEY = 'rapport_export_prefs_v1';
const DEFAULT_PREFS = { groupe: true, sections: SECTIONS.map(s => s[0]), annexes: [] };

const loadPrefs = () => {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'), annexes: [] }; } catch { return DEFAULT_PREFS; }
};

export const ExportPdfDialog = ({ open, onOpenChange, candidats, onExport, exporting }) => {
  const [prefs, setPrefs] = useState(loadPrefs);
  const toggle = (list, v) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  const setSections = (sections) => setPrefs(p => ({ ...p, sections }));
  const setAnnexes = (annexes) => setPrefs(p => ({ ...p, annexes }));
  const allAnnexes = candidats.length > 0 && prefs.annexes.length === candidats.length;
  const submit = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ groupe: prefs.groupe, sections: prefs.sections }));
    onExport({ groupe: prefs.groupe, sections: prefs.sections, annexes: allAnnexes ? 'all' : prefs.annexes.join(',') });
  };
  const disabled = (!prefs.groupe || prefs.sections.length === 0) && prefs.annexes.length === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-pdf-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Export PDF — contenu du rapport</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-2">
            <label className="flex items-center gap-2 font-semibold text-slate-800">
              <Checkbox checked={prefs.groupe} onCheckedChange={(v) => setPrefs(p => ({ ...p, groupe: !!v }))} data-testid="export-groupe-toggle" /> Rapport de groupe
            </label>
            <div className={`pl-6 space-y-1.5 ${prefs.groupe ? '' : 'opacity-40 pointer-events-none'}`}>
              <div className="flex gap-3 text-xs text-indigo-600 mb-1">
                <button type="button" onClick={() => setSections(SECTIONS.map(s => s[0]))} data-testid="export-sections-all">Tout</button>
                <button type="button" onClick={() => setSections([])} data-testid="export-sections-none">Aucun</button>
              </div>
              {SECTIONS.map(([k, l]) => (
                <label key={k} className="flex items-start gap-2 text-slate-700">
                  <Checkbox className="mt-0.5" checked={prefs.sections.includes(k)} onCheckedChange={() => setSections(toggle(prefs.sections, k))} data-testid={`export-section-${k}`} /> <span>{l}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="font-semibold text-slate-800">Annexes individuelles <span className="text-xs font-normal text-slate-500">(1 page par apprenant)</span></p>
            <div className="flex gap-3 text-xs text-indigo-600">
              <button type="button" onClick={() => setAnnexes(candidats.map(c => c.candidat_id))} data-testid="export-annexes-all">Tous</button>
              <button type="button" onClick={() => setAnnexes([])} data-testid="export-annexes-none">Aucun</button>
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
              {candidats.map(c => (
                <label key={c.candidat_id} className={`flex items-center gap-2 px-2 py-1.5 text-slate-700 ${c.abandon ? 'text-slate-400' : ''}`}>
                  <Checkbox checked={prefs.annexes.includes(c.candidat_id)} onCheckedChange={() => setAnnexes(toggle(prefs.annexes, c.candidat_id))} data-testid={`export-annexe-${c.candidat_id}`} />
                  <span>{c.nom} {c.prenom}</span>{c.abandon && <span className="text-[10px] ml-auto">abandon</span>}
                </label>
              ))}
              {candidats.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun candidat</p>}
            </div>
            <p className="text-[11px] text-slate-500">Décochez « Rapport de groupe » pour n'exporter que les annexes sélectionnées.</p>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button type="button" onClick={submit} disabled={disabled || exporting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="export-pdf-confirm">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />} Générer le PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
